import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Row, Typography } from 'antd';
import { ReactComponent as PrintIcon } from '../../Images/PrintIcon.svg';
import { TOLERANCE_OPTIONS } from '../../constants/toleranceOptions';
import dayjs from 'dayjs';
import { fetchViewWorkOrderById } from '../../slices/work-order';
import logo from '../../Images/logo.svg';

const PrintWorkOrder = () => {
  const contentRef = useRef();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [workOrderData, setWorkOrderData] = useState(null);
  const { user: currentUser } = useSelector(state => state.auth);
  const { Title, Text } = Typography;

  const fetchData = async () => {
    try {
      const response = await dispatch(
        fetchViewWorkOrderById({ id, print: true }),
      ).unwrap();
      setWorkOrderData(response?.results?.data?.[0] || null);
    } catch (error) {
      console.error('Failed to fetch work order:', error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  
  const hasSurfaceTreatment =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) &&
        item.jobworks.includes('Surface treatment'),
    ) || false;
  const hasAnodising =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) &&
        item.jobworks.includes('Surface treatment') &&
        item.anodising,
    ) || false;
  const hasPowderCoating =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) &&
        item.jobworks.includes('Surface treatment') &&
        item.powder_coating,
    ) || false;
  const hasPVDF =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) &&
        item.jobworks.includes('Surface treatment') &&
        item.pvdf,
    ) || false;
  const hasEngineering =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) && item.jobworks.includes('Engineering'),
    ) || false;
  const hasCutting =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) &&
        item.jobworks.includes('Engineering') &&
        item.cutting,
    ) || false;
  const hasMachinings =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) &&
        item.jobworks.includes('Engineering') &&
        item.machinings,
    ) || false;
  const hasDeburring =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) &&
        item.jobworks.includes('Engineering') &&
        item.deburring,
    ) || false;
  const hasLaserMarking =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) &&
        item.jobworks.includes('Laser marking'),
    ) || false;
  const hasLaserDescription =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) &&
        item.jobworks.includes('Laser marking') &&
        item.laser_marking_description,
    ) || false;
  const hasLaserPrice =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) &&
        item.jobworks.includes('Laser marking') &&
        item.laser_marking_price,
    ) || false;
  const hasMillFinish =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) && item.jobworks.includes('Mill Finish'),
    ) || false;
  const hasOutSource =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) && item.jobworks.includes('Out Source'),
    ) || false;
  const hasThermalBreak =
    workOrderData?.work_order_details?.some(
      item =>
        Array.isArray(item?.jobworks) &&
        item.jobworks.includes('Thermal Break'),
    ) || false;

  const hasOnlyMillFinish = workOrderData?.work_order_details?.every(item => {
    return (
      Array.isArray(item.jobworks) &&
      item.jobworks.length === 1 &&
      item.jobworks[0] === 'Mill Finish'
    );
  });

  const showJobWorkColumn =
    workOrderData?.work_order_details?.some(item => {
      if (!Array.isArray(item?.jobworks)) return false;
      if (item.jobworks.length === 1 && item.jobworks.includes('Mill Finish'))
        return false;
      return item.jobworks.length > 0;
    }) || false;

  const getSurfaceTreatmentChildren = title => {
    const children = [];
    if (hasAnodising) {
      children.push({
        title: 'Anodising Description',
        dataIndex: 'anodising_description',
        key: 'anodising_description',
        align: 'center',
        width: 70,
        render: (_, record) =>
          Array.isArray(record?.jobworks) &&
            record.jobworks.includes('Surface treatment') &&
            record.anodising
            ? _
            : '-',
      });
      if (title === 'ACCOUNT/SALES COPY') {
        children.push({
          title: 'Anodising Price',
          dataIndex: 'anodising_price',
          key: 'anodising_price',
          align: 'center',
          width: 60,
          render: (_, record) =>
            Array.isArray(record?.jobworks) &&
              record.jobworks.includes('Surface treatment') &&
              record.anodising
              ? _
              : '-',
        });
      }
    }
    if (hasPowderCoating) {
      children.push({
        title: 'Powder Coating Description',
        dataIndex: 'powder_coating_description',
        key: 'powder_coating_description',
        align: 'center',
        width: 70,
        render: (_, record) =>
          Array.isArray(record?.jobworks) &&
            record.jobworks.includes('Surface treatment') &&
            record.powder_coating
            ? _
            : '-',
      });
      if (title === 'ACCOUNT/SALES COPY') {
        children.push({
          title: 'Powder Coating Price',
          dataIndex: 'powder_coating_price',
          key: 'powder_coating_price',
          align: 'center',
          width: 60,
          render: (_, record) =>
            Array.isArray(record?.jobworks) &&
              record.jobworks.includes('Surface treatment') &&
              record.powder_coating
              ? _
              : '-',
        });
      }
    }
    if (hasPVDF) {
      children.push({
        title: 'PVDF Description',
        dataIndex: 'pvdf_description',
        key: 'pvdf_description',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobworks) &&
            record.jobworks.includes('Surface treatment') &&
            record.pvdf
            ? _
            : '-',
      });
      if (title === 'ACCOUNT/SALES COPY') {
        children.push({
          title: 'PVDF Price',
          dataIndex: 'pvdf_price',
          key: 'pvdf_price',
          align: 'center',
          width: 55,
          render: (_, record) =>
            Array.isArray(record?.jobworks) &&
              record.jobworks.includes('Surface treatment') &&
              record.pvdf
              ? _
              : '-',
        });
      }
    }
    return children;
  };

  const getEngineeringChildren = title => {
    const children = [];
    if (hasCutting) {
      children.push({
        title: 'Cutting',
        dataIndex: 'cutting',
        key: 'cutting',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobworks) &&
            record.jobworks.includes('Engineering') &&
            record.cutting
            ? title === 'ACCOUNT/SALES COPY'
              ? record.cutting_price
              : 'Yes'
            : '-',
      });
    }
    if (hasMachinings) {
      children.push({
        title: 'Machinings',
        dataIndex: 'machinings',
        key: 'machinings',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobworks) &&
            record.jobworks.includes('Engineering') &&
            record.machinings
            ? title === 'ACCOUNT/SALES COPY'
              ? record.machinings_price
              : 'Yes'
            : '-',
      });
    }
    if (hasDeburring) {
      children.push({
        title: 'Deburring',
        dataIndex: 'deburring',
        key: 'deburring',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobworks) &&
            record.jobworks.includes('Engineering') &&
            record.deburring
            ? title === 'ACCOUNT/SALES COPY'
              ? record.deburring_price
              : 'Yes'
            : '-',
      });
    }
    return children;
  };

  const getLaserMarkingChildren = title => {
    const children = [];
    if (hasLaserDescription) {
      children.push({
        title: 'Laser Marking Description',
        dataIndex: 'laser_marking_description',
        key: 'laser_marking_description',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobworks) &&
            record.jobworks.includes('Laser marking') &&
            record.laser_marking_description
            ? _
            : '-',
      });
    }
    if (hasLaserPrice && title === 'ACCOUNT/SALES COPY') {
      children.push({
        title: 'Laser Marking Price',
        dataIndex: 'laser_marking_price',
        key: 'laser_marking_price',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobworks) &&
            record.jobworks.includes('Laser marking') &&
            record.laser_marking_price
            ? _
            : '-',
      });
    }
    return children;
  };

  const isFixedPrice = workOrderData?.nalco_type?.toLowerCase() === 'fixed';

  const getDimensions = record => {
    if (!record?.die_profile) return 'N/A';
    const dimensions = [
      record.die_profile.dimension1,
      record.die_profile.dimension2,
      record.die_profile.dimension3,
      record.die_profile.dimension4,
    ]
      .filter(dim => dim && dim !== '0.00')
      .map(dim => Number(dim)?.toFixed(2))
      .join(' X ') + ' MM';
    return dimensions || 'N/A';
  };

  const getAlloyTemper = record => {
    const alloyName = record?.alloy?.alloy_code || '';
    const standardName = record?.alloy?.standard_name || '';
    const temperName = record?.temper?.name || '';
    const temperCode = record?.temper?.code || '';
    return (
      `${alloyName} (${standardName}) / ${temperName} (${temperCode})` || 'N/A'
    );
  };

  const getPrice = record => {
    const isFixed = workOrderData?.nalco_rate?.toLowerCase() === 'fixed';
    const isVariable = workOrderData?.nalco_rate?.toLowerCase() === 'variable';
    const conversion = record?.conversion ?? '0.00';
    if (isFixed) return `${record?.nalco_rate ?? '0.00'}+${conversion}`;
    if (isVariable) return `Variable+${conversion}`;
    return '0.00';
  };

  const getJobWorkOrder = () => {
    const jobWorkOrder = [];
    const seenJobWorks = new Set();

    workOrderData?.work_order_details?.forEach(item => {
      if (Array.isArray(item.jobworks)) {
        item.jobworks.forEach(job => {
          if (!seenJobWorks.has(job)) {
            jobWorkOrder.push(job);
            seenJobWorks.add(job);
          }
        });
      }
    });

    return jobWorkOrder;
  };

  const getJobWorkColumns = title => {
    const jobWorkOrder = getJobWorkOrder();
    const columns = [];

    jobWorkOrder.forEach(jobType => {
      switch (jobType) {
        case 'Surface treatment':
          if (hasSurfaceTreatment) {
            columns.push({
              title: 'Surface Treatment',
              align: 'center',
              width: 200,
              children: getSurfaceTreatmentChildren(title),
            });
          }
          break;
        case 'Engineering':
          if (hasEngineering) {
            columns.push({
              title: 'Engineering',
              align: 'center',
              width: 150,
              children: getEngineeringChildren(title),
            });
          }
          break;
        case 'Laser marking':
          if (hasLaserMarking) {
            columns.push({
              title: 'Laser Marking',
              align: 'center',
              width: 150,
              children: getLaserMarkingChildren(title),
            });
          }
          break;
        case 'Mill Finish':
          if (hasMillFinish) {
            columns.push({
              title: 'Mill Finish',
              dataIndex: 'mill_finish',
              key: 'mill_finish',
              align: 'center',
              width: 50,
              render: (_, record) =>
                Array.isArray(record?.jobworks) &&
                  record.jobworks.includes('Mill Finish')
                  ? 'Yes'
                  : null,
            });
          }
          break;
        case 'Out Source':
          if (hasOutSource) {
            columns.push({
              title: 'Out Source',
              dataIndex: 'out_source',
              key: 'out_source',
              align: 'center',
              width: 50,
              render: (_, record) =>
                Array.isArray(record?.jobworks) &&
                  record.jobworks.includes('Out Source')
                  ? record.out_source === true
                    ? 'Yes'
                    : record.out_source === false
                      ? 'No'
                      : ''
                  : '',
            });
          }
          break;
        case 'Thermal Break':
          if (hasThermalBreak) {
            columns.push({
              title: 'Thermal Break',
              dataIndex: 'thermal_break',
              key: 'thermal_break',
              align: 'center',
              width: 50,
              render: (_, record) =>
                Array.isArray(record?.jobworks) &&
                  record.jobworks.includes('Thermal Break')
                  ? 'Yes'
                  : null,
            });
          }
          break;
        default:
          break;
      }
    });

    return columns;
  };

  const columns = title => {
    const jobWorkColumns = getJobWorkColumns(title);
    const jobWorkLeafCount = jobWorkColumns.reduce(
      (sum, col) => sum + (col.children ? col.children.length : 1),
      0,
    );

    const baseColumns = [
      {
        title: 'SR No.',
        dataIndex: 'sr',
        width: 40,
        align: 'center',
        className: 'col-sr',
        render: (_text, _record, index) => index + 1,
      },
      {
        title: 'SECTION NO.',
        dataIndex: 'section',
        width: 60,
        align: 'center',
        className: 'col-profile',
        render: (_text, _record) => (
          <span>{_record?.die_profile?.die_number || 'N/A'}</span>
        ),
      },
      {
        title: 'IMAGE',
        dataIndex: 'image',
        width: 80,
        align: 'center',
        className: 'summary-cell-center col-image',
        render: (_text, _record) => {
          const fileUrl = _record?.die_profile?.die_diagram;
          const imageContainerStyle = {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '4px',
            overflow: 'hidden',
          };
          const imageStyle = {
            width: '60px',
            height: '60px',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
          };
          if (!fileUrl) {
            return (
              <div style={imageContainerStyle}>
                <span style={{ fontSize: '16px', color: '#999' }}>N/A</span>
              </div>
            );
          }
          const fileExtension = fileUrl.split('.').pop().toLowerCase();
          if (fileExtension === 'pdf') {
            return (
              <Button
                type="link"
                style={{ padding: '0px' }}
                onClick={async () => {
                  const response = await fetch(fileUrl);
                  if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                  }
                  const blobData = await response.blob();
                  const blob = new Blob([blobData], { type: 'application/pdf' });
                  const blobURL = URL.createObjectURL(blob);
                  window.open(blobURL);
                }}
              >
                View PDF
              </Button>
            );
          } else if (
            ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExtension)
          ) {
            return (
              <div style={imageContainerStyle}>
                <img src={fileUrl} alt="Die Diagram" style={imageStyle} />
              </div>
            );
          } else {
            return (
              <div style={imageContainerStyle}>
                <span style={{ fontSize: '12px', color: '#999' }}>Unsupported</span>
              </div>
            );
          }
        },
      },
      {
        title: 'DESCRIPTION',
        dataIndex: 'description',
        width: 90,
        align: 'center',
        className: 'col-desc',
        render: (_text, _record) => <div>{getDimensions(_record)}</div>,
      },
      {
        title: 'WT RANGE',
        dataIndex: 'weight_range',
        width: 55,
        align: 'center',
        className: 'col-weight-range',
        render: value => value || 'N/A',
      },
      {
        title: 'LENGTH',
        dataIndex: 'length',
        width: 55,
        align: 'center',
        className: 'col-length',
        render: value => parseInt(value)?.toFixed(0) || '0',
      },
      {
        title: 'PIECES',
        width: 130,
        children: [
          {
            title: 'Order',
            dataIndex: 'pieces',
            width: 30,
            align: 'center',
            className: 'col-pieces',
            render: value => value || '0',
          },
          {
            title: title === 'PACKING COPY' ? 'Packed' : 'Produced',
            dataIndex: 'pieces_processed',
            width: 30,
            align: 'center',
            className: 'col-pieces-processed',
            render: () => '0',
          },
        ],
      },
      {
        title: 'WEIGHT (kg.)',
        width: 130,
        children: [
          {
            title: 'Order',
            dataIndex: 'net_weight',
            width: 30,
            align: 'center',
            className: 'col-weight',
            render: value => (value ? Number(value).toFixed(3) : '0.00'),
          },
          {
            title: title === 'PACKING COPY' ? 'Packed' : 'Produced',
            dataIndex: 'weight_processed',
            width: 30,
            align: 'center',
            className: 'col-weight-processed',
            render: () => '0.00',
          },
        ],
      },
      {
        title: 'ALLOY/TEMP-GRADE',
        dataIndex: 'grade',
        width: 95,
        align: 'center',
        className: 'col-alloy',
        render: (_text, _record) => <div>{getAlloyTemper(_record)}</div>,
      },
    ];

    const jobWorkColumnsFormatted = showJobWorkColumn
      ? [
        {
          title: 'Job Work',
          align: 'center',
          width: jobWorkLeafCount * 50,
          children: getJobWorkColumns(title),
        },
      ].filter(Boolean)
      : [];

    const priceColumns = ['ACCOUNT/SALES COPY'].includes(title)
      ? [
        {
          title: 'PRICE/KG (Rs.)',
          width: 150,
          children: [
            {
              title: 'Item Rate',
              dataIndex: 'price_kg',
              width: 50,
              className: 'col-conv',
              align: 'center',
              render: (_, record) => getPrice(record),
            },
            {
              title: 'Packing Charges',
              dataIndex: 'packing_charges',
              width: 40,
              className: 'col-packing',
              align: 'center',
              render: (_, record) =>
                record.packing_charge
                  ? Number(record.packing_charge).toFixed(2)
                  : '0.00',
            },
          ],
        },
      ]
      : [];

    const amountColumns = title === 'ACCOUNT/SALES COPY' && isFixedPrice
      ? [
        {
          title: 'AMOUNT (Rs.)',
          dataIndex: 'total_basic_amount',
          width: 90,
          className: 'col-basic',
          align: 'right',
          render: value => (value ? Number(value).toFixed(2) : '0.00'),
        },
        {
          title: 'IGST (18%)',
          dataIndex: 'gst_amount',
          width: 80,
          className: 'col-gst',
          align: 'right',
          render: value => (value ? Number(value).toFixed(2) : '0.00'),
        },
        {
          title: 'TOTAL',
          dataIndex: 'total_amount',
          width: 80,
          className: 'col-total',
          align: 'right',
          render: value => (value ? Number(value).toFixed(2) : '0.00'),
        },
      ]
      : [];

    return [...baseColumns, ...jobWorkColumnsFormatted, ...priceColumns, ...amountColumns];
  };

  const getMaxColumnDepth = cols => {
    let maxDepth = 1;
    cols.forEach(col => {
      if (col.children) {
        const childDepth = getMaxColumnDepth(col.children) + 1;
        maxDepth = Math.max(maxDepth, childDepth);
      }
    });
    return maxDepth;
  };

  const renderHeaderRows = cols => {
    const maxDepth = getMaxColumnDepth(cols);
    const headerRows = [];

    const collectHeadersAtDepth = (columns, depth, currentDepth = 0) => {
      const headers = [];

      columns.forEach((col, index) => {
        const colSpan = col.children ? col.children.length : 1;

        if (currentDepth === depth) {
          headers.push(
            <th
              key={col.key || index}
              colSpan={colSpan}
              rowSpan={col.children ? 1 : maxDepth - currentDepth}
              style={{
                border: '1px solid #dee2e6',
                padding: '12px 8px',
                backgroundColor: '#f8f9fa',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '14px',
              }}
            >
              {typeof col.title === 'function' ? col.title() : col.title}
            </th>,
          );
        } else if (col.children) {
          headers.push(...collectHeadersAtDepth(col.children, depth, currentDepth + 1));
        }
      });

      return headers;
    };

    for (let depth = 0; depth < maxDepth; depth++) {
      headerRows.push(
        <tr key={`header-row-${depth}`} style={{ backgroundColor: '#f8f9fa' }}>
          {collectHeadersAtDepth(cols, depth)}
        </tr>,
      );
    }

    return headerRows;
  };

  const getLeafColumns = cols => {
    const leafColumns = [];

    cols.forEach(column => {
      if (column.children) {
        column.children.forEach(child => {
          if (child.children) {
            leafColumns.push(...child.children);
          } else {
            leafColumns.push(child);
          }
        });
      } else {
        leafColumns.push(column);
      }
    });

    return leafColumns;
  };

  const renderCellContent = (column, record, index) => {
    if (column.render) {
      const result = column.render(record[column.dataIndex], record, index);
      return React.isValidElement(result) ? result : String(result || '');
    }
    return record[column.dataIndex] || '';
  };

  const printStyles = `
        @media print {
            * {
                margin: 0 !important;
                padding: 0 !important;
                box-sizing: border-box !important;
            }
            
            body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
                font-size: 12px !important;
                line-height: 1.4 !important;
                color: #000 !important;
                background: white !important;
            }
            
            .print-container-quotation {
                padding: 20px !important;
                width: 100% !important;
                max-width: none !important;
                margin: 0 !important;
                background: white !important;
                box-shadow: none !important;
                page-break-before: always !important; /* Ensure each copy starts on a new page */
            }
            
            .terms-and-bank-container {
                padding: 20px !important;
                width: 100% !important;
                page-break-before: always !important;
            }
            
            @page {
                size: A4 !important;
                margin: 15mm !important;
            }
            
            .main-heading {
                font-size: 28px !important;
                font-weight: 600 !important;
                text-align: center !important;
                margin: 20px 0 !important;
                color: #000 !important;
            }
            
            .header-text {
                font-size: 14px !important;
                line-height: 1.5 !important;
            }
            
            .customer-details-header {
                font-weight: 600 !important;
                font-size: 16px !important;
                margin-bottom: 8px !important;
                color: #333 !important;
            }
            
            table {
                width: 100% !important;
                border-collapse: collapse !important;
                margin: 20px 0 !important;
                border: 1px solid #c0bfbfff !important;
            }
            
            table th,
            table td {
                border: 1px solid #c0bfbfff !important;
                padding: 8px 6px !important;
                text-align: center !important;
                vertical-align: middle !important;
                font-size: 11px !important;
                word-wrap: break-word !important;
            }
            
            table thead th {
                background-color: #ffffffff !important;
                font-weight: 600 !important;
                font-size: 12px !important;
                border-bottom: 2px solid #c0bfbfff !important;
            }
            
            table thead {
                display: table-header-group !important;
            }
            
            .summary-row td {
                background-color: #ffffffff !important;
                font-weight: 600 !important;
                font-size: 12px !important;
            }
            
            .signature-section {
                margin-top: 60px !important;
                display: flex !important;
                justify-content: space-between !important;
            }
            
            .signature-section h5 {
                font-size: 14px !important;
                color: #12406a !important;
                margin-top: 40px !important;
            }
            
            .terms-heading {
                font-size: 18px !important;
                font-weight: 700 !important;
                text-align: center !important;
                color: #12406a !important;
                margin-bottom: 15px !important;
            }
            
            .terms__content {
                font-size: 13px !important;
                line-height: 1.1 !important;
                text-align: justify !important;
            }
            
            .bankDetails {
                background: #ffffffff !important;
                padding: 15px !important;
                border-left: 4px solid #ffffffff !important;
                margin-top: 20px !important;
            }
            
            .bankDetails b {
                font-weight: 600 !important;
            }
            
            .no-print {
                display: none !important;
            }
            
            .logo-section img {
                max-width: 180px !important;
                height: auto !important;
            }
            
            .ant-row {
                display: flex !important;
                width: 100% !important;
            }
            
            .ant-col {
                flex: 1 !important;
            }
        }
    `;

  const commonStyles = `
    * {
      font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
    }
    body {
      font-size: 16px !important;
      font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
    }
    .main-container {
      width: 100%;
      padding: 0;
    }
    .ant-col {
      font-size: 16px !important;
    }
    .h1, .h2, .h3, .h4, .h5, .h6, h1, h2, h3, h4, h5, h6 {
      margin-bottom: .5rem;
      font-family: inherit;
      font-weight: 500;
      font-size: 16px;
      line-height: 1.2;
      color: inherit;
    }
    table {
      color: black;
      text-align: center;
      border-collapse: collapse;
    }
    table, th, td {
      text-align: center;
      border-collapse: collapse;
    }
    .table-bordered {
      border: 1px solid #dee2e6;
    }
    .table-bordered td, .table-bordered th {
      border: 1px solid #dee2e6;
    }
    .table-bordered td, .table-bordered thead th {
      border-bottom-width: 2px;
    }
    .table thead th {
      vertical-align: bottom;
      border-bottom: 2px solid #dee2e6;
    }
    .table-bordered th {
      border: 1px solid #dee2e6;
    }
    .table td, .table th {
      padding: .75rem;
      vertical-align: top;
      border-top: 1px solid #dee2e6;
    }
    .header-section {
      text-align: center;
      margin-bottom: 20px;
    }
    .main-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    .sub-header {
      font-size: 24px;
      font-weight: bold;
      margin: 0;
    }
    .copy-label {
      float: right;
      font-size: 18px;
      font-weight: normal;
    }
    .info-section {
      display: flex;
      justify-content: space-between;
      margin: 20px 0;
      font-size: 16px;
    }
    .info-section div {
      font-size: 16px;
    }
    .signature-section {
      margin-top: 100px;
      font-size: 16px;
    }
  `;

  const memoPrintStyles = useMemo(() => printStyles, []);
  const memoCommonStyles = useMemo(() => commonStyles, []);

  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = memoPrintStyles + memoCommonStyles;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, [memoPrintStyles, memoCommonStyles]);

  const handlePrint = () => {
    window.print();
  };

  const renderAllCopies = () => {
    const copies = [
      hasPermission('print_production_copy') && 'PRODUCTION COPY',
      hasPermission('print_packing_copy') && 'PACKING COPY',
      hasPermission('print_account_sales_copy') && 'ACCOUNT/SALES COPY',
    ].filter(Boolean);

    return copies.map((copy, index) => (
      <div key={copy} className="copy-wrapper">
        {renderCopy(copy, copy === 'ACCOUNT/SALES COPY' && isFixedPrice)}
      </div>
    ));
  };

  const renderCopy = (title, includePriceColumns = false) => {
    const cols = columns(title);
    const leafColumns = getLeafColumns(cols);
    const jobWorkLeafCount = getJobWorkColumns(title).reduce(
      (sum, col) => sum + (col.children ? col.children.length : 1),
      0,
    );

    return (
      <div className="print-container-quotation" style={{ padding: '20px', background: 'white' }}>
        <div className="page-content">
          <Row justify="space-between" align="middle" style={{ marginBottom: '40px', marginTop: '5px' }}>
            <Col span={24} className="logo-section" style={{ textAlign: 'right' }}>
              <img src={logo} alt="Company Logo" style={{ maxWidth: '180px' }} />
            </Col>
          </Row>

          <Row justify="space-between" align="middle" style={{ marginBottom: '40px' }}>
            <Col span={24}>
              <div style={{ textAlign: 'center' }}>
                <h1 className="main-heading">Work Order</h1>
                <span className="copy-label">{title}</span>
              </div>
            </Col>
          </Row>

          <Row justify="space-between" align="top" style={{ marginBottom: '40px', width: '100%' }}>
            <Col span={12}>
              <div className="customer-details">
                <h6 className="customer-details-header">Bill To</h6>
                <span className="header-text">
                  {workOrderData?.bill_to?.customer_name || 'N/A'}
                  <br />
                  {[
                    workOrderData?.bill_to?.factory_address?.shop,
                    workOrderData?.bill_to?.factory_address?.area,
                    workOrderData?.bill_to?.factory_address?.landmark,
                    workOrderData?.bill_to?.factory_address?.city,
                    workOrderData?.bill_to?.factory_address?.state,
                    workOrderData?.bill_to?.factory_address?.pin_code,
                  ]
                    .filter(part => part && part.toLowerCase() !== 'na' && part.trim() !== '')
                    .join(', ') || 'N/A'}
                </span>
              </div>
            </Col>
            <Col span={12} style={{ textAlign: 'right' }} className="date-quotation">
              <div>
                <span className="header-text">
                  <strong>Order Date: </strong>
                  {workOrderData?.order_date
                    ? dayjs(workOrderData.order_date).format('DD-MM-YYYY')
                    : 'N/A'}
                </span>
              </div>
              <div>
                <span className="header-text">
                  <strong>Order No: </strong>
                  {workOrderData?.order_no || 'N/A'}
                </span>
              </div>
            </Col>
          </Row>

          <Row justify="space-between" align="top" style={{ marginBottom: '10px', width: '100%' }}>
            <Col span={12}>
              <div className="customer-details">
                <h6 className="customer-details-header">Ship To</h6>
                <div className="customer-details-content">
                  <span className="header-text">
                    {workOrderData?.ship_to?.customer_name || 'N/A'}
                    <br />
                    {[
                      workOrderData?.ship_to?.factory_address?.shop,
                      workOrderData?.ship_to?.factory_address?.area,
                      workOrderData?.ship_to?.factory_address?.landmark,
                      workOrderData?.ship_to?.factory_address?.city,
                      workOrderData?.ship_to?.factory_address?.state,
                      workOrderData?.ship_to?.factory_address?.pin_code,
                    ]
                      .filter(part => part && part.toLowerCase() !== 'na' && part.trim() !== '')
                      .join(', ') || 'N/A'}
                  </span>
                </div>
              </div>
            </Col>
            <Col span={12} style={{ textAlign: 'right' }} className="kind-attention">
              <div className="customer-details">
                <div className="customer-details-header">Purchase Order</div>
                <div className="customer-details-content">
                  <span className="header-text">
                    <strong>Date: </strong>
                    {workOrderData?.purchase_order_date
                      ? dayjs(workOrderData.purchase_order_date).format('DD-MM-YYYY')
                      : 'N/A'}
                    <br />
                    <strong>Number: </strong>
                    {workOrderData?.purchase_order_no || 'N/A'}
                  </span>
                </div>
              </div>
            </Col>
          </Row>

          <div style={{ width: '100%', marginBottom: '10px' }}>
            <table
              style={{
                width: '100%',
                border: '1px solid #dee2e6',
                marginTop: '30px',
                borderCollapse: 'collapse',
              }}
            >
              <thead>{renderHeaderRows(cols)}</thead>
              <tbody>
                {workOrderData?.work_order_details?.map((record, rowIndex) => (
                  <tr key={record.id || rowIndex}>
                    {leafColumns.map((column, colIndex) => (
                      <td
                        key={column.key || colIndex}
                        className={column.className}
                        style={{
                          border: '1px solid #dee2e6',
                          padding: '12px 8px',
                          textAlign: column.align || 'center',
                          fontSize: '14px',
                        }}
                      >
                        {renderCellContent(column, record, rowIndex)}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="summary-row">
                  <td
                    colSpan={6}
                    style={{
                      border: '1px solid #dee2e6',
                      backgroundColor: '#e9ecef',
                      fontWeight: '600',
                      textAlign: 'center',
                    }}
                  >
                    <strong>Total</strong>
                  </td>
                  <td
                    style={{
                      border: '1px solid #dee2e6',
                      backgroundColor: '#e9ecef',
                      fontWeight: '600',
                      textAlign: 'center',
                    }}
                  >
                    <strong>{workOrderData?.total_pieces || '0'} Nos.</strong>
                  </td>
                  <td
                    style={{
                      border: '1px solid #dee2e6',
                      backgroundColor: '#e9ecef',
                      fontWeight: '600',
                      textAlign: 'center',
                    }}
                  >
                    <strong>0</strong>
                  </td>
                  <td
                    style={{
                      border: '1px solid #dee2e6',
                      backgroundColor: '#e9ecef',
                      fontWeight: '600',
                      textAlign: 'center',
                    }}
                  >
                    <strong>
                      {workOrderData?.total_weight
                        ? Number(workOrderData.total_weight).toFixed(3)
                        : '0.000'}{' '}
                      Kg.
                    </strong>
                  </td>
                  <td
                    style={{
                      border: '1px solid #dee2e6',
                      backgroundColor: '#e9ecef',
                      fontWeight: '600',
                      textAlign: 'center',
                    }}
                  >
                    <strong>0.00</strong>
                  </td>
                  {jobWorkLeafCount > 0 && (
                    <td
                      colSpan={jobWorkLeafCount + (hasOnlyMillFinish ? 0 : 1)}
                      style={{
                        border: '1px solid #dee2e6',
                        backgroundColor: '#e9ecef',
                      }}
                    />
                  )}
                  {['ACCOUNT/SALES COPY'].includes(title) && (
                    <>
                      <td
                        style={{
                          border: '1px solid #dee2e6',
                          backgroundColor: '#e9ecef',
                          fontWeight: '600',
                          textAlign: 'right',
                        }}
                      >
                        <strong></strong>
                      </td>
                      <td
                        style={{
                          border: '1px solid #dee2e6',
                          backgroundColor: '#e9ecef',
                          fontWeight: '600',
                          textAlign: 'right',
                        }}
                      >
                        <strong></strong>
                      </td>
                    </>
                  )}
                  {includePriceColumns && (
                    <>
                      <td
                        style={{
                          border: '1px solid #dee2e6',
                          backgroundColor: '#e9ecef',
                          fontWeight: '600',
                          textAlign: 'right',
                        }}
                      >
                        <strong>
                          {workOrderData?.work_order_details
                            ?.reduce(
                              (sum, item) => sum + (Number(item.total_basic_amount) || 0),
                              0,
                            )
                            .toFixed(2) || '0.00'}
                        </strong>
                      </td>
                      <td
                        style={{
                          border: '1px solid #dee2e6',
                          backgroundColor: '#e9ecef',
                          fontWeight: '600',
                          textAlign: 'right',
                        }}
                      >
                        <strong>
                          {workOrderData?.work_order_details
                            ?.reduce(
                              (sum, item) => sum + (Number(item.gst_amount) || 0),
                              0,
                            )
                            .toFixed(2) || '0.00'}
                        </strong>
                      </td>
                      <td
                        style={{
                          border: '1px solid #dee2e6',
                          backgroundColor: '#e9ecef',
                          fontWeight: '600',
                          textAlign: 'right',
                        }}
                      >
                        <strong>
                          {workOrderData?.work_order_details
                            ?.reduce(
                              (sum, item) => sum + (Number(item.total_amount) || 0),
                              0,
                            )
                            .toFixed(2) || '0.00'}
                        </strong>
                      </td>
                    </>
                  )}
                </tr>
                {title === 'ACCOUNT/SALES COPY' && isFixedPrice && (
                  <tr className="summary-row">
                    <td
                      colSpan={leafColumns.length - 1}
                      style={{
                        border: '1px solid #dee2e6',
                        backgroundColor: '#e9ecef',
                        fontWeight: '600',
                        textAlign: 'right',
                      }}
                    >
                      <strong>Total Payable Amount :</strong>
                    </td>
                    <td
                      style={{
                        border: '1px solid #dee2e6',
                        backgroundColor: '#e9ecef',
                        fontWeight: '600',
                        textAlign: 'right',
                      }}
                    >
                      <strong>
                        {workOrderData?.work_order_details
                          ?.reduce(
                            (sum, item) => sum + (Number(item.total_amount) || 0),
                            0,
                          )
                          .toFixed(2) || '0.00'}
                      </strong>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginBottom: '20px', width: '100%' }}>
            <Row justify="space-between" className="info-section">
              <Col span={12}>
                <div>
                  <strong>Supply Tolerance: </strong>
                  {TOLERANCE_OPTIONS.find(
                    opt => opt.value === workOrderData?.tolerance,
                  )?.label || 'N/A'}
                </div>
                <div>
                  <strong>Delivery Date: </strong>
                  {workOrderData?.delivery_date
                    ? dayjs(workOrderData.delivery_date).format('DD-MM-YYYY')
                    : 'N/A'}
                </div>
                <div>
                  <strong>Quantity Control: </strong>As Per INDIAN STANDARDS (IS)
                </div>
                <div>
                  <strong>Remarks: </strong>{workOrderData?.remarks || 'N/A'}
                </div>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <div>
                  <strong>Packing Type: </strong>
                  {workOrderData?.packing_type?.length
                    ? workOrderData.packing_type.join(', ')
                    : 'N/A'}
                </div>
                <div>
                  <strong>Transporter Name: </strong>N/A
                </div>
                <div>
                  <strong>Vehicle No.: </strong>N/A
                </div>
              </Col>
            </Row>
          </div>

          <div
            className="signature-section"
            style={{ marginTop: '80px', display: 'flex', justifyContent: 'space-between' }}
          >
            <div style={{ textAlign: 'center', flex: 1 }}>
              <Title
                level={5}
                style={{ color: '#12406a', margin: '5px 0', marginTop: '60px' }}
              >
                Processed By
                <br />
                (Sales)
              </Title>
            </div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <Title
                level={5}
                style={{ color: '#12406a', margin: '5px 0', marginTop: '60px' }}
              >
                Checked By
                <br />
                (Design Dept.)
              </Title>
            </div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <Title
                level={5}
                style={{ color: '#12406a', margin: '5px 0', marginTop: '60px' }}
              >
                Approved By
                <br />
                (Sales Head)
              </Title>
            </div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <Title
                level={5}
                style={{ color: '#12406a', margin: '5px 0', marginTop: '60px' }}
              >
                Approved By
                <br />
                (Director)
              </Title>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const hasPermission = permission => {
    return (
      currentUser?.permissions?.permissions?.workorder?.includes(permission) ||
      false
    );
  };

  if (!workOrderData) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
        }}
      >
        <div style={{ fontSize: '18px' }}>Loading work order data...</div>
      </div>
    );
  }

  return (
    <>
      <div ref={contentRef}>
        {renderAllCopies()}
        <div
          className="no-print"
          style={{
            textAlign: 'center',
            marginTop: '20px',
            padding: '20px',
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 1000,
          }}
        >
          <Button
            type="primary"
            size="large"
            onClick={handlePrint}
            icon={<PrintIcon />}
            style={{
              boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
              borderRadius: '8px',
            }}
          >
            Print Work Order
          </Button>
        </div>
      </div>
    </>
  );
};

export default PrintWorkOrder;